// 優惠 — automatic promotions the register applies by itself (promoEngine.js):
// % off, $ off, special price, 買X送Y, 滿件折, quantity tiers; by whole shop,
// category, brand, product or tag; with optional code, minimum spend, dates,
// weekdays, usage limit. Rows are snake_case and upserted straight into the
// promotions table (see pos_phase3.sql §5).
import { useEffect, useMemo, useState } from "react";
import { zhEn } from "./posTier.js";
import { Plus, Search, Trash2, RefreshCw, TicketPercent, Check, X } from "lucide-react";
import { C, Btn, Chip, Pill, Seg, Card, Sheet, Label, Field, Toggle, Empty, money, inputStyle, useWide } from "./posUi.jsx";
import { posErrorText, newId } from "./posMath.js";
import { ProductThumb } from "./Register.jsx";
import { ToggleRow } from "./Products.jsx";
import { fetchPromotions, savePromotion, deletePromotion, fetchCategories, fetchBrands } from "../../lib/api.js";
import { describePromotion, localDate, promoIsLive } from "./promoEngine.js";

const TYPES = [
  ["percent", zhEn("折扣 %", "Discount %"), "🏷️"],
  ["amount", zhEn("減價 $", "$ off"), "💵"],
  ["fixed_price", zhEn("特價", "Sale price"), "💲"],
  ["bogo", zhEn("買X送Y", "Buy X get Y"), "🎁"],
  ["bundle", zhEn("滿件折", "Multi-buy discount"), "🛍️"],
  ["tiered", zhEn("數量階梯", "Quantity tiers"), "📶"],
];
const TYPE_ICON = Object.fromEntries(TYPES.map(([k, , e]) => [k, e]));
const TARGETS = [["all", zhEn("全店", "Storewide")], ["category", zhEn("分類", "Categories")], ["brand", zhEn("品牌", "Brands")], ["product", zhEn("商品", "Products")], ["tag", zhEn("標籤", "Labels")]];
const DOW = [zhEn("日", "Sun"), zhEn("一", "Mon"), zhEn("二", "Tue"), zhEn("三", "Wed"), zhEn("四", "Thu"), zhEn("五", "Fri"), zhEn("六", "Sat")];

// One-tap starting points (like PetPOS). Every template is just a pre-filled
// editor — the shop still picks the category / products and saves.
//   第二件半價 → bogo {buyQty 1, freeQty 1, percent 50}: every 2nd unit gets
//     50% off (the engine's bogo `percent` = discount on the "free" units).
//     A bundle {buyQty 2, percent 25} would only match at even quantities.
//   滿 $300 減 $30 → amount 30 + target all + min_spend 300 + mixGroup: with
//     mixGroup the engine takes the $30 ONCE off the whole basket (split
//     across the lines by value) instead of $30 off every line.
const TEMPLATES = [
  { key: "all10", icon: "🏷️", title: zhEn("全店 9 折", "Storewide 10% off"), sub: zhEn("所有商品", "All products"), row: { name: zhEn("全店 9 折", "Storewide 10% off"), type: "percent", target: "all", params: { percent: 10 } } },
  { key: "cat", icon: "🗂️", title: zhEn("指定分類 X 折", "Category X% off"), sub: zhEn("揀分類 + 折扣", "Pick a category + discount"), row: { name: zhEn("分類折扣", "Category discount"), type: "percent", target: "category", params: { percent: 20 } } },
  { key: "b2g1", icon: "🎁", title: zhEn("買 2 送 1", "Buy 2 get 1"), sub: zhEn("可混款", "Mix & match"), row: { name: zhEn("買 2 送 1", "Buy 2 get 1"), type: "bogo", target: "category", params: { buyQty: 2, freeQty: 1, mixGroup: true } } },
  { key: "half", icon: "✌️", title: zhEn("第二件半價", "2nd item half price"), sub: zhEn("每 2 件，其中 1 件半價", "Of every 2 items, 1 is half price"), row: { name: zhEn("第二件半價", "2nd item half price"), type: "bogo", target: "category", params: { buyQty: 1, freeQty: 1, percent: 50, mixGroup: true } } },
  { key: "spend", icon: "💵", title: zhEn("滿 $300 減 $30", "$30 off when you spend $300"), sub: zhEn("整單減一次", "Taken off once per ticket"), row: { name: zhEn("滿 $300 減 $30", "$30 off when you spend $300"), type: "amount", target: "all", min_spend: 300, params: { amount: 30, mixGroup: true } } },
  { key: "fixed", icon: "💲", title: zhEn("特價 $X", "Special price $X"), sub: zhEn("指定商品一口價", "Fixed price on chosen products"), row: { name: zhEn("特價", "Sale price"), type: "fixed_price", target: "product", params: { price: "" } } },
  { key: "tier", icon: "📶", title: zhEn("數量階梯", "Quantity tiers"), sub: zhEn("買 2 件 9 折、3 件 85 折", "Buy 2 for 10% off, 3 for 15% off"), row: { name: zhEn("數量階梯", "Quantity tiers"), type: "tiered", target: "category", params: { mixGroup: true, tiers: [{ min: 2, percent: 10 }, { min: 3, percent: 15 }] } } },
  { key: "blank", icon: "✏️", title: zhEn("自訂", "Custom"), sub: zhEn("由零開始", "Start from scratch"), row: { name: "", type: "percent", target: "all", params: { percent: "" } } },
];

const blankForm = (tenantId) => ({
  id: null, tenant_id: tenantId, name: "", display_name: "", type: "percent", target: "all", target_ids: [], params: {},
  code: "", code_required: false, min_spend: "", starts_on: "", ends_on: "", days_of_week: [], exclusive: false,
  exclude_on_sale: false, max_uses: "", used_count: 0, status: "active", sort_order: 0,
});

// Row (from the DB) → editable form (strings for inputs).
const toForm = (r) => ({
  ...blankForm(r.tenant_id), ...r,
  display_name: r.display_name || "",
  target_ids: Array.isArray(r.target_ids) ? r.target_ids.map(String) : [],
  params: { ...(r.params || {}) },
  code: r.code || "",
  min_spend: Number(r.min_spend) ? String(r.min_spend) : "",
  starts_on: r.starts_on || "",
  ends_on: r.ends_on || "",
  days_of_week: Array.isArray(r.days_of_week) ? r.days_of_week.map(Number) : [],
  max_uses: r.max_uses == null ? "" : String(r.max_uses),
});

// Strict number parse for form fields: null = empty, NaN = not a number
// ("1..5", "abc", "1e"), else the number.
const NUM_RE = /^-?(\d+\.?\d*|\.\d+)$/;
const numOrNull = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  return NUM_RE.test(s) ? Number(s) : NaN;
};
const tierIsAmount = (t) => t._mode === "amount" || (t._mode !== "percent" && numOrNull(t.amount) != null && numOrNull(t.percent) == null);
const cleanParams = (type, p) => {
  const n = (v) => { const x = numOrNull(v); return Number.isFinite(x) ? x : 0; };
  const mix = !!p.mixGroup;
  switch (type) {
    case "percent": return { percent: n(p.percent) };
    case "amount": return { amount: n(p.amount), mixGroup: mix };
    case "fixed_price": return { price: n(p.price) };
    case "bogo": return { buyQty: Math.floor(n(p.buyQty)), freeQty: Math.floor(n(p.freeQty)), mixGroup: mix, ...(Number.isFinite(numOrNull(p.percent)) && n(p.percent) < 100 ? { percent: n(p.percent) } : null) };
    case "bundle": return { buyQty: Math.floor(n(p.buyQty)), percent: n(p.percent), mixGroup: mix };
    case "tiered": return {
      mixGroup: mix,
      tiers: (p.tiers || [])
        .map(t => (tierIsAmount(t) ? { min: Math.floor(n(t.min)), amount: n(t.amount) } : { min: Math.floor(n(t.min)), percent: n(t.percent) }))
        .sort((a, b) => a.min - b.min),
    };
    default: return {};
  }
};

// Form → snake_case row for savePromotion (upserted as-is).
const toRow = (f, tenantId) => ({
  id: f.id || undefined,
  tenant_id: tenantId,
  name: (f.name || "").trim() || (f.display_name || "").trim(),
  display_name: (f.display_name || "").trim(),
  type: f.type,
  target: f.target,
  target_ids: f.target === "all" ? [] : f.target_ids,
  params: cleanParams(f.type, f.params || {}),
  code: (f.code || "").trim().toUpperCase(),
  code_required: !!f.code_required && !!(f.code || "").trim(),
  min_spend: Number.isFinite(numOrNull(f.min_spend)) ? Math.max(0, numOrNull(f.min_spend)) : 0,
  starts_on: f.starts_on || null,
  ends_on: f.ends_on || null,
  days_of_week: [...(f.days_of_week || [])].sort(),
  exclusive: !!f.exclusive,
  exclude_on_sale: !!f.exclude_on_sale,
  // empty = unlimited; 0 stays 0 (= paused by limit), never turned into unlimited
  max_uses: Number.isFinite(numOrNull(f.max_uses)) && numOrNull(f.max_uses) >= 0 ? Math.floor(numOrNull(f.max_uses)) : null,
  status: f.status === "inactive" ? "inactive" : "active",
  sort_order: Number(f.sort_order) || 0,
});

const exhausted = (p) => p.max_uses != null && p.max_uses !== "" && Number(p.used_count || 0) >= Number(p.max_uses);
// List grouping. 進行中 = would apply today (promoIsLive — the same test the
// register uses) or running but not on today's weekday.
const phaseOf = (p, ds) => {
  if (p.status !== "active" || (p.ends_on && ds > p.ends_on) || exhausted(p)) return "done";
  if (p.starts_on && ds < p.starts_on) return "soon";
  return "live";
};

const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const randomCode = (n = 8) => {
  let s = "";
  const rnd = typeof crypto !== "undefined" && crypto.getRandomValues ? crypto.getRandomValues(new Uint32Array(n)) : null;
  for (let i = 0; i < n; i++) s += CODE_CHARS[(rnd ? rnd[i] : Math.floor(Math.random() * 1e9)) % CODE_CHARS.length];
  return s;
};

const saveErrorText = (e) => /promotions_code_uniq|duplicate key|23505/.test(String(e?.message || e)) ? zhEn("優惠碼已經用緊", "That promo code is already in use") : posErrorText(e);

// readOnly: staff without the discount permission (the server refuses their
// writes anyway) — list only. onChange: called after every successful write
// so the register can reload its promotions.
export default function Promotions({ tenant, sym = "$", products = [], toast, readOnly = false, onChange }) {
  const wide = useWide(900);
  const [rows, setRows] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [picker, setPicker] = useState(false);
  const [edit, setEdit] = useState(null);
  const tid = tenant?.id;

  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!tid) return undefined;
    let off = false;
    Promise.all([fetchPromotions(tid), fetchCategories(tid).catch(() => []), fetchBrands(tid).catch(() => [])])
      .then(([ps, cs, bs]) => { if (off) return; setErr(""); setRows(ps || []); setCategories(cs || []); setBrands(bs || []); })
      .catch(e => { if (off) return; setErr(posErrorText(e)); setRows(r => r || []); });
    return () => { off = true; };
  }, [tid, reload]);
  const load = () => setReload(n => n + 1);

  // Without the category / brand tables (older data) fall back to the labels
  // on products — the engine also matches by name.
  const catOpts = useMemo(() => categories.length ? categories.map(c => ({ id: String(c.id), name: c.name }))
    : [...new Set(products.map(p => p.category).filter(Boolean))].sort().map(n => ({ id: n, name: n })), [categories, products]);
  const brandOpts = useMemo(() => brands.length ? brands.map(b => ({ id: String(b.id), name: b.name }))
    : [...new Set(products.map(p => p.brand).filter(Boolean))].sort().map(n => ({ id: n, name: n })), [brands, products]);
  const tagOpts = useMemo(() => [...new Set(products.flatMap(p => p.tags || []).filter(Boolean))].sort().map(t => ({ id: t, name: "#" + t })), [products]);
  const ctx = useMemo(() => ({ categories: catOpts, brands: brandOpts, products, sym }), [catOpts, brandOpts, products, sym]);

  const ds = localDate();
  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = (rows || []).filter(p => !s || [p.name, p.display_name, p.code].some(x => String(x || "").toLowerCase().includes(s)));
    const g = { live: [], soon: [], done: [] };
    for (const p of list) g[phaseOf(p, ds)].push(p);
    return g;
  }, [rows, q, ds]);

  const upsertLocal = (saved) => setRows(rs => {
    const has = (rs || []).some(r => r.id === saved.id);
    return has ? rs.map(r => r.id === saved.id ? saved : r) : [saved, ...(rs || [])];
  });

  const toggleStatus = async (p, on) => {
    const next = { ...p, status: on ? "active" : "inactive" };
    upsertLocal(next);
    try {
      const saved = await savePromotion(toRow(toForm(next), tid));
      if (saved) upsertLocal(saved);
      onChange?.();
      toast?.(on ? zhEn("已啟用「", "Turned on \"") + (p.display_name || p.name) + zhEn("」", "\"") : zhEn("已停用「", "Turned off \"") + (p.display_name || p.name) + zhEn("」", "\""));
    } catch (e) { upsertLocal(p); toast?.(saveErrorText(e), "error"); }
  };

  const startFrom = (tpl) => {
    const f = { ...blankForm(tid), ...tpl.row, params: JSON.parse(JSON.stringify(tpl.row.params || {})) };
    f.display_name = f.name;
    if (f.min_spend) f.min_spend = String(f.min_spend);
    setPicker(false);
    setEdit(f);
  };

  const section = (key, title, list) => list.length > 0 && (
    <div key={key} style={{ marginBottom: 16 }}>
      <Label style={{ margin: "0 4px 8px" }}>{title} · {list.length}</Label>
      <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(2,minmax(0,1fr))" : "minmax(0,1fr)", gap: 8 }}>
        {list.map(p => <PromoCard key={p.id} p={p} ds={ds} ctx={ctx} readOnly={readOnly} onOpen={readOnly ? undefined : () => setEdit(toForm(p))} onToggle={(on) => toggleStatus(p, on)} />)}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <div style={{ flex: 1, position: "relative" }}>
          <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("搵優惠名稱或優惠碼", "Search promotion name or code")} style={{ ...inputStyle, paddingLeft: 38, borderRadius: 14 }} />
        </div>
        {!readOnly && <Btn onClick={() => setPicker(true)} style={{ padding: "12px 16px", borderRadius: 14 }}><Plus size={18} /> {zhEn("新增優惠", "Add promotion")}</Btn>}
      </div>
      {readOnly && <p style={{ background: C.amberSoft, color: "#B45309", fontWeight: 800, fontSize: 12.5, borderRadius: 12, padding: "9px 12px", margin: "0 0 12px" }}>{zhEn("只有店主或有折扣權限嘅員工可以改優惠", "Only the owner or staff with discount permission can change promotions")}</p>}

      {err && <p style={{ background: C.redSoft, color: C.red, fontWeight: 800, fontSize: 13, borderRadius: 12, padding: "10px 12px", margin: "0 0 12px" }}>{err} <button type="button" onClick={load} style={{ border: "none", background: "none", color: C.red, fontWeight: 900, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>{zhEn("再試", "Retry")}</button></p>}

      {rows === null ? <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, padding: 40 }}>{zhEn("載入中…", "Loading…")}</p>
        : rows.length === 0 ? (
          <Empty icon="🎉" title={zhEn("未有優惠", "No promotions yet")} sub={zhEn("設定好之後，收銀會自動計折扣，唔使逐單打折", "Once set up, the till applies the discount automatically — no need to discount each sale")}
            action={!readOnly && <Btn small onClick={() => setPicker(true)}><Plus size={16} /> {zhEn("新增第一個優惠", "Add your first promotion")}</Btn>} />
        ) : (
          <>
            {section("live", zhEn("進行中", "Running"), groups.live)}
            {section("soon", zhEn("未開始", "Not started"), groups.soon)}
            {section("done", zhEn("已結束或停用", "Ended or turned off"), groups.done)}
            {!groups.live.length && !groups.soon.length && !groups.done.length && <Empty icon="🔍" title={zhEn("搵唔到優惠", "No promotions found")} />}
          </>
        )}

      <Sheet open={picker} onClose={() => setPicker(false)} title={zhEn("新增優惠", "Add promotion")} maxWidth={620}>
        <p style={{ fontSize: 13, fontWeight: 700, color: C.sub, margin: "0 2px 12px" }}>{zhEn("揀一個範本，之後可以再改。", "Pick a template — you can change it afterwards.")}</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8 }}>
          {TEMPLATES.map(t => (
            <button key={t.key} type="button" className="pos-tap" onClick={() => startFrom(t)} style={{ textAlign: "left", border: "1px solid " + C.line, background: "#fff", borderRadius: 18, padding: 12, fontFamily: "inherit", cursor: "pointer", boxShadow: "0 3px 10px rgba(74,74,104,0.04)" }}>
              <div style={{ fontSize: 26, marginBottom: 4 }}>{t.icon}</div>
              <p style={{ margin: 0, fontSize: 14.5, fontWeight: 900, color: C.ink }}>{t.title}</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, fontWeight: 700, color: C.sub }}>{t.sub}</p>
            </button>
          ))}
        </div>
      </Sheet>

      {edit && !readOnly && (
        <PromoEditor form={edit} tenantId={tid} sym={sym} ctx={ctx} catOpts={catOpts} brandOpts={brandOpts} tagOpts={tagOpts} products={products}
          onClose={() => setEdit(null)}
          onSaved={(saved, isNew) => { upsertLocal(saved); setEdit(null); onChange?.(); toast?.(isNew ? zhEn("已新增優惠", "Promotion added") : zhEn("已儲存", "Saved")); }}
          onDeleted={(id) => { setRows(rs => (rs || []).filter(r => r.id !== id)); setEdit(null); onChange?.(); toast?.(zhEn("已刪除", "Deleted")); }} />
      )}
    </div>
  );
}

function PromoCard({ p, ds, ctx, onOpen, onToggle, readOnly }) {
  const ph = phaseOf(p, ds);
  const title = p.display_name || p.name;
  return (
    <Card onClick={onOpen} style={{ padding: 12, display: "flex", gap: 12, alignItems: "flex-start", opacity: ph === "done" ? 0.72 : 1 }}>
      <div style={{ width: 44, height: 44, borderRadius: 14, background: ph === "live" ? C.accentSoft : "#F3F2F8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{TYPE_ICON[p.type] || "🏷️"}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</p>
        {p.display_name && p.name && p.display_name !== p.name && <p style={{ margin: 0, fontSize: 11.5, fontWeight: 700, color: C.faint }}>{p.name}</p>}
        <p style={{ margin: "3px 0 6px", fontSize: 12.5, fontWeight: 700, color: C.sub, lineHeight: 1.4 }}>{describePromotion(p, ctx)}</p>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {p.code && <Pill tone={p.code_required ? "pink" : "grey"}><TicketPercent size={11} /> {p.code}</Pill>}
          {p.max_uses != null
            ? <Pill tone={exhausted(p) ? "red" : "grey"}>{zhEn("已用", "Used")} {p.used_count || 0} / {p.max_uses}</Pill>
            : Number(p.used_count) > 0 && <Pill>{zhEn("已用", "Used")} {p.used_count} {zhEn("次", "times")}</Pill>}
          {ph === "soon" && <Pill tone="blue">{p.starts_on} {zhEn("開始", "starts")}</Pill>}
          {ph === "live" && !promoIsLive(p) && <Pill tone="grey">{zhEn("今日唔適用", "Not valid today")}</Pill>}
          {p.status === "active" && ph === "done" && <Pill tone="amber">{exhausted(p) ? zhEn("已用完", "Used up") : zhEn("已過期", "Expired")}</Pill>}
          {p.exclusive && <Pill tone="amber">{zhEn("獨享", "Exclusive")}</Pill>}
        </div>
      </div>
      {readOnly
        ? <Pill tone={p.status === "active" ? "green" : "grey"}>{p.status === "active" ? zhEn("啟用", "On") : zhEn("停用", "Off")}</Pill>
        : (
          <div onClick={e => e.stopPropagation()} style={{ paddingTop: 2 }}>
            <Toggle on={p.status === "active"} onChange={onToggle} />
          </div>
        )}
    </Card>
  );
}

function PromoEditor({ form, tenantId, sym, ctx, catOpts, brandOpts, tagOpts, products, onClose, onSaved, onDeleted }) {
  const isNew = !form.id;
  const [f, setF] = useState(form);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (patch) => setF(x => ({ ...x, ...patch }));
  const pr = f.params || {};
  const setP = (patch) => setF(x => ({ ...x, params: { ...(x.params || {}), ...patch } }));
  const row = toRow(f, tenantId);
  const summary = describePromotion(row, ctx);

  const changeType = (type) => {
    if (type === f.type) return;
    const defaults = { percent: { percent: 10 }, amount: { amount: 20 }, fixed_price: { price: "" }, bogo: { buyQty: 2, freeQty: 1, mixGroup: true }, bundle: { buyQty: 2, percent: 10, mixGroup: true }, tiered: { mixGroup: true, tiers: [{ min: 2, percent: 10 }, { min: 3, percent: 15 }] } };
    set({ type, params: defaults[type] });
  };

  const validate = () => {
    if (!row.name) return zhEn("請輸入優惠名稱", "Enter a promotion name");
    const raw = f.params || {};
    // Every non-empty number field must be a real number ("1..5", "abc" → error).
    const num = (v) => numOrNull(v);
    const bad = (v) => Number.isNaN(num(v));
    const pct = (v) => Number.isFinite(num(v)) && num(v) > 0 && num(v) <= 100;
    const int1 = (v) => Number.isFinite(num(v)) && Number.isInteger(num(v)) && num(v) >= 1;
    if (f.type === "percent" && !pct(raw.percent)) return zhEn("折扣 % 要喺 0 以上、100 或以下", "Discount % must be above 0 and at most 100");
    if (f.type === "amount" && !(Number.isFinite(num(raw.amount)) && num(raw.amount) > 0)) return zhEn("請輸入正確嘅減價金額", "Enter a valid amount off");
    if (f.type === "fixed_price" && !(Number.isFinite(num(raw.price)) && num(raw.price) >= 0)) return zhEn("請輸入正確嘅特價", "Enter a valid special price");
    if (f.type === "bogo") {
      if (!int1(raw.buyQty) || !int1(raw.freeQty)) return zhEn("買 / 送 件數要係 1 或以上嘅整數", "Buy / free quantities must be whole numbers of 1 or more");
      if (num(raw.percent) != null && !pct(raw.percent)) return zhEn("優惠件折扣 % 要喺 0 以上、100 或以下（留空 = 免費）", "Discount % on the promo item must be above 0 and at most 100 (empty = free)");
    }
    if (f.type === "bundle") {
      if (!int1(raw.buyQty)) return zhEn("買滿件數要係 1 或以上嘅整數", "The quantity to buy must be a whole number of 1 or more");
      if (!pct(raw.percent)) return zhEn("折扣 % 要喺 0 以上、100 或以下", "Discount % must be above 0 and at most 100");
    }
    if (f.type === "tiered") {
      const tiers = Array.isArray(raw.tiers) ? raw.tiers : [];
      if (!tiers.length) return zhEn("最少要一個階梯", "At least one tier is needed");
      for (const t of tiers) {
        if (!int1(t.min)) return zhEn("階梯件數要係 1 或以上嘅整數", "Tier quantities must be whole numbers of 1 or more");
        const isAmt = tierIsAmount(t);
        if (isAmt ? !(Number.isFinite(num(t.amount)) && num(t.amount) > 0) : !pct(t.percent)) return zhEn("階梯折扣要大過 0（% 最多 100）", "Tier discounts must be above 0 (% at most 100)");
      }
      const mins = tiers.map(t => num(t.min));
      if (new Set(mins).size !== mins.length) return zhEn("階梯件數唔可以重複", "Tier quantities can't repeat");
    }
    if (f.target !== "all" && !f.target_ids.length) return zhEn("請揀適用嘅", "Choose the applicable ") + (TARGETS.find(t => t[0] === f.target)?.[1] || zhEn("項目", "Items"));
    if (bad(f.min_spend) || (num(f.min_spend) ?? 0) < 0) return zhEn("最低消費要係 0 或以上嘅數字", "Minimum spend must be 0 or more");
    if (bad(f.max_uses) || (num(f.max_uses) != null && !(Number.isInteger(num(f.max_uses)) && num(f.max_uses) >= 0))) return zhEn("使用次數要係 0 或以上嘅整數（留空 = 無限）", "Number of uses must be a whole number of 0 or more (empty = unlimited)");
    if (f.code_required && !(f.code || "").trim()) return zhEn("需要優惠碼就要填優惠碼", "Enter a code if a code is required");
    if (f.starts_on && f.ends_on && f.ends_on < f.starts_on) return zhEn("結束日期早過開始日期", "The end date is before the start date");
    return "";
  };

  const save = async () => {
    const v = validate();
    if (v) { setErr(v); return; }
    setBusy(true); setErr("");
    try {
      const saved = await savePromotion({ ...row, id: row.id || newId() });
      onSaved(saved || row, isNew);
    } catch (e) { setErr(saveErrorText(e)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(zhEn("刪除優惠「", "Delete promotion \"") + (f.display_name || f.name) + zhEn("」？已賣出嘅單會保留優惠記錄。", "\"? Past sales keep their promotion record."))) return;
    setBusy(true);
    try { await deletePromotion(f.id); onDeleted(f.id); } catch (e) { setErr(posErrorText(e)); setBusy(false); }
  };

  const opts = f.target === "category" ? catOpts : f.target === "brand" ? brandOpts : f.target === "tag" ? tagOpts : null;

  return (
    <Sheet open onClose={busy ? undefined : onClose} title={isNew ? zhEn("新增優惠", "Add promotion") : zhEn("編輯優惠", "Edit promotion")} maxWidth={640}
      footer={
        <div>
          {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, margin: "0 0 8px", textAlign: "center" }}>{err}</p>}
          <div style={{ display: "flex", gap: 8 }}>
            {!isNew && <Btn kind="danger" onClick={del} disabled={busy} style={{ padding: "14px 16px" }}><Trash2 size={18} /></Btn>}
            <Btn style={{ flex: 1 }} onClick={save} disabled={busy}>{busy ? zhEn("儲存中…", "Saving…") : zhEn("儲存", "Save")}</Btn>
          </div>
        </div>
      }>
      {/* live summary */}
      <div style={{ background: C.accentSoft, borderRadius: 16, padding: "12px 14px", marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 11.5, fontWeight: 900, color: C.accent }}>{TYPE_ICON[f.type]} {zhEn("顧客會見到：", "Customers see:")}{(f.display_name || f.name || "").trim() || zhEn("（未命名）", "(unnamed)")}</p>
        <p style={{ margin: "3px 0 0", fontSize: 14, fontWeight: 900, color: C.ink, lineHeight: 1.45 }}>{summary}</p>
      </div>

      <Seg value={f.status} onChange={v => set({ status: v })} style={{ marginBottom: 14 }} options={[["active", zhEn("🟢 啟用", "🟢 On")], ["inactive", zhEn("⏸ 停用", "⏸ Off")]]} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label={zhEn("內部名稱 *", "Internal name *")} value={f.name} onChange={e => set({ name: e.target.value })} placeholder={zhEn("例如 十月小食推廣", "e.g. October treats promo")} />
        <Field label={zhEn("收據 / 購物車顯示", "Shown on receipt / cart")} value={f.display_name} onChange={e => set({ display_name: e.target.value })} placeholder={f.name || zhEn("例如 買 2 送 1", "e.g. Buy 2 get 1")} />
      </div>

      <Label>{zhEn("優惠類型", "Promotion type")}</Label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {TYPES.map(([k, l, e]) => <Chip key={k} on={f.type === k} onClick={() => changeType(k)}>{e} {l}</Chip>)}
      </div>

      <ParamsEditor type={f.type} pr={pr} setP={setP} sym={sym} />

      <Label style={{ marginTop: 6 }}>{zhEn("適用於", "Applies to")}</Label>
      <Seg value={f.target} onChange={v => set({ target: v, target_ids: [] })} style={{ marginBottom: 10 }} options={TARGETS} />
      {f.target !== "all" && (
        <TargetPicker kind={f.target} value={f.target_ids} onChange={ids => set({ target_ids: ids })} opts={opts} products={products} sym={sym} />
      )}
      {f.target !== "product" && <ToggleRow label={zhEn("特價品除外", "Excludes sale items")} sub={zhEn("已經減價（商品特價）嘅貨唔再計優惠", "Items already on sale (product sale price) don't get the promotion")} on={f.exclude_on_sale} onChange={v => set({ exclude_on_sale: v })} />}

      <Label style={{ marginTop: 16 }}>{zhEn("條件", "Conditions")}</Label>
      <Field label={zhEn("最低消費（", "Minimum spend (") + sym + zhEn("，零售小計）", ", retail subtotal)")} inputMode="decimal" value={f.min_spend} onChange={e => set({ min_spend: e.target.value })} placeholder={zhEn("0 = 冇要求", "0 = no minimum")} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label={zhEn("開始日期", "Start date")} type="date" value={f.starts_on || ""} onChange={e => set({ starts_on: e.target.value })} style={{ fontSize: 14, padding: "11px 8px" }} />
        <Field label={zhEn("結束日期", "End date")} type="date" value={f.ends_on || ""} onChange={e => set({ ends_on: e.target.value })} style={{ fontSize: 14, padding: "11px 8px" }} />
      </div>
      <Label>{zhEn("逢星期（唔揀 = 每日）", "Days of the week (none = every day)")}</Label>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {DOW.map((d, i) => {
          const on = f.days_of_week.includes(i);
          return <button key={i} type="button" onClick={() => set({ days_of_week: on ? f.days_of_week.filter(x => x !== i) : [...f.days_of_week, i] })}
            style={{ flex: 1, height: 40, borderRadius: 12, border: "none", fontFamily: "inherit", fontSize: 14, fontWeight: 900, cursor: "pointer", background: on ? C.accent : "#F3F2F8", color: on ? "#fff" : (i === 0 || i === 6 ? C.accent : C.ink) }}>{d}</button>;
        })}
      </div>

      <Label>{zhEn("優惠碼", "Promo code")}</Label>
      <Field value={f.code} onChange={e => set({ code: e.target.value.toUpperCase().replace(/\s+/g, "") })} placeholder={zhEn("例如 SUMMER（可留空）", "e.g. SUMMER (optional)")}
        hint={f.code_required ? zhEn("收銀要輸入呢個碼先會有優惠", "The cashier must enter this code for the promotion to apply") : f.code ? zhEn("唔使輸入都自動有優惠；個碼只作記錄", "Applies automatically without entering it; the code is just for reference") : undefined}
        right={<button type="button" aria-label={zhEn("隨機產生", "Generate randomly")} onClick={() => set({ code: randomCode() })} style={{ border: "none", background: C.accentSoft, color: C.accent, borderRadius: 10, width: 38, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><RefreshCw size={17} /></button>} />
      <ToggleRow label={zhEn("需要輸入優惠碼", "Require the code")} sub={zhEn("關閉 = 符合條件就自動套用", "Off = applies automatically when the conditions are met")} on={f.code_required} onChange={v => set({ code_required: v, code: v && !f.code ? randomCode() : f.code })} />

      <Label style={{ marginTop: 16 }}>{zhEn("進階", "Advanced")}</Label>
      <ToggleRow label={zhEn("獨享優惠", "Exclusive promotion")} sub={zhEn("同一件貨，獨享優惠永遠優先於其他優惠", "On the same item, an exclusive promotion always beats the others")} on={f.exclusive} onChange={v => set({ exclusive: v })} />
      <div style={{ height: 10 }} />
      <Field label={zhEn("最多使用次數（每張單計一次）", "Maximum uses (counted once per sale)")} inputMode="numeric" value={f.max_uses} onChange={e => set({ max_uses: e.target.value.replace(/[^\d]/g, "") })} placeholder={zhEn("留空 = 無限", "Empty = unlimited")}
        hint={!isNew ? zhEn("已用 ", "Used ") + (f.used_count || 0) + zhEn(" 次", " times") : undefined} />
      <Field label={zhEn("排序（細數先）", "Order (smaller first)")} inputMode="numeric" value={String(f.sort_order ?? 0)} onChange={e => set({ sort_order: parseInt(e.target.value, 10) || 0 })} />
      <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "4px 2px 0", lineHeight: 1.5 }}>
        {zhEn("每件貨只會用一個優惠（揀最抵嗰個）。收銀員自己改價或者打折嘅貨唔會再計優惠。", "Each item gets one promotion (the best one). Items where the cashier changed the price or gave a discount don't get promotions.")}
      </p>
    </Sheet>
  );
}

const numField = (label, value, onChange, extra = {}) => (
  <Field label={label} inputMode="decimal" value={value ?? ""} onChange={e => onChange(e.target.value)} {...extra} />
);

function ParamsEditor({ type, pr, setP, sym }) {
  const mixRow = (label, sub) => <ToggleRow label={label} sub={sub} on={!!pr.mixGroup} onChange={v => setP({ mixGroup: v })} />;
  if (type === "percent") return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {[5, 10, 15, 20, 30, 50].map(p => <Chip key={p} on={Number(pr.percent) === p} onClick={() => setP({ percent: p })}>{p}% off</Chip>)}
      </div>
      {numField(zhEn("折扣 %", "Discount %"), pr.percent, v => setP({ percent: v }), { placeholder: zhEn("例如 10 = 9 折", "e.g. 10 = 10% off") })}
    </div>
  );
  if (type === "amount") return (
    <div>
      {numField(zhEn("減幾多（", "Amount off (") + sym + zhEn("）", ")"), pr.amount, v => setP({ amount: v }))}
      {mixRow(zhEn("整單合計減一次", "Take off once for the whole total"), pr.mixGroup ? zhEn("適用商品加埋一齊減一次（例如 滿 $300 減 $30）", "Taken off once across all eligible items (e.g. $30 off when you spend $300)") : zhEn("而家：購物車每一項各自減", "Now: taken off each cart line separately"))}
    </div>
  );
  if (type === "fixed_price") return numField(zhEn("特價（每件 ", "Special price (each ") + sym + zhEn("）", ")"), pr.price, v => setP({ price: v }), { hint: zhEn("原價高過呢個價嘅貨，每件以呢個價計", "Items priced above this are charged at this price") });
  if (type === "bogo") return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {numField(zhEn("買", "Buy"), pr.buyQty, v => setP({ buyQty: v }))}
        {numField(zhEn("送 / 優惠件數", "Free / promo quantity"), pr.freeQty, v => setP({ freeQty: v }))}
        {numField(zhEn("優惠件折扣 %", "Discount % on promo item"), pr.percent ?? "", v => setP({ percent: v }), { placeholder: "100" })}
      </div>
      <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "-6px 2px 8px" }}>{zhEn("折扣 % 留空 = 送嘅件數免費；買 1 送 1 + 50% = 第二件半價。最平嘅件數先當送。", "Leave Discount % empty = the free items are free; buy 1 get 1 + 50% = 2nd item half price. The cheapest items count as the free ones.")}</p>
      {mixRow(zhEn("可混款", "Mix & match"), zhEn("唔同款適用商品可以一齊計件數", "Eligible items of different variants count together"))}
    </div>
  );
  if (type === "bundle") return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {numField(zhEn("買滿幾件", "Quantity to buy"), pr.buyQty, v => setP({ buyQty: v }))}
        {numField(zhEn("全部折扣 %", "Discount % on all"), pr.percent, v => setP({ percent: v }))}
      </div>
      {mixRow(zhEn("可混款", "Mix & match"), zhEn("唔同款適用商品可以一齊計件數", "Eligible items of different variants count together"))}
    </div>
  );
  if (type === "tiered") {
    const tiers = Array.isArray(pr.tiers) ? pr.tiers : [];
    const setTier = (i, patch) => setP({ tiers: tiers.map((t, j) => j === i ? { ...t, ...patch } : t) });
    return (
      <div>
        {tiers.map((t, i) => {
          const isAmt = tierIsAmount(t);
          return (
            <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-end", marginBottom: 8 }}>
              <div style={{ width: 82 }}><Field label={zhEn("買滿（件）", "Buy (qty)")} inputMode="numeric" value={t.min ?? ""} onChange={e => setTier(i, { min: e.target.value })} style={{ marginBottom: 0 }} /></div>
              <div style={{ width: 92, marginBottom: 12 }}>
                <Seg value={isAmt ? "amount" : "percent"} style={{ padding: 2 }}
                  onChange={m => setTier(i, m === "amount" ? { _mode: "amount", amount: t.amount ?? "", percent: "" } : { _mode: "percent", percent: t.percent ?? "", amount: "" })}
                  options={[["percent", "%"], ["amount", sym]]} />
              </div>
              <div style={{ flex: 1 }}>
                {isAmt
                  ? <Field label={zhEn("減", "Less")} inputMode="decimal" value={t.amount ?? ""} onChange={e => setTier(i, { amount: e.target.value })} />
                  : <Field label={zhEn("折扣 %", "Discount %")} inputMode="decimal" value={t.percent ?? ""} onChange={e => setTier(i, { percent: e.target.value })} />}
              </div>
              <button type="button" aria-label={zhEn("刪除階梯", "Delete tier")} onClick={() => setP({ tiers: tiers.filter((_, j) => j !== i) })} style={{ marginBottom: 12, width: 40, height: 44, borderRadius: 12, border: "none", background: C.redSoft, color: C.red, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><X size={18} /></button>
            </div>
          );
        })}
        <Btn kind="soft" small onClick={() => { const last = tiers[tiers.length - 1]; setP({ tiers: [...tiers, { min: (Number(last?.min) || 1) + 1, percent: "" }] }); }} style={{ marginBottom: 10 }}><Plus size={15} /> {zhEn("加階梯", "Add tier")}</Btn>
        {mixRow(zhEn("可混款", "Mix & match"), zhEn("唔同款適用商品可以一齊計件數", "Eligible items of different variants count together"))}
      </div>
    );
  }
  return null;
}

// Searchable multi-select: categories / brands / tags (opts) or products.
function TargetPicker({ kind, value, onChange, opts, products, sym }) {
  const [q, setQ] = useState("");
  const sel = new Set(value);
  const toggle = (id) => onChange(sel.has(id) ? value.filter(x => x !== id) : [...value, id]);
  const isProd = kind === "product";
  const all = useMemo(() => isProd
    ? products.filter(p => p.status !== "draft" && p.sellable !== false).map(p => ({ id: String(p.id), name: p.name, p }))
    : (opts || []), [isProd, products, opts]);
  const s = q.trim().toLowerCase();
  const list = all.filter(o => !s || o.name.toLowerCase().includes(s) || (o.p && ((o.p.sku || "").toLowerCase().includes(s) || o.p.barcode === s || (o.p.brand || "").toLowerCase().includes(s))));
  const shown = [...list.filter(o => sel.has(o.id)), ...list.filter(o => !sel.has(o.id))].slice(0, 80);
  const nameOf = (id) => all.find(o => o.id === id)?.name || id;
  const noun = { category: zhEn("分類", "Categories"), brand: zhEn("品牌", "Brands"), product: zhEn("商品", "Products"), tag: zhEn("標籤", "Labels") }[kind];

  return (
    <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: 10, marginBottom: 10 }}>
      {value.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {value.map(id => (
            <button key={id} type="button" onClick={() => toggle(id)} style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", borderRadius: 99, padding: "5px 10px", background: C.accentSoft, color: C.accent, fontFamily: "inherit", fontSize: 12.5, fontWeight: 900, cursor: "pointer", maxWidth: "100%" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nameOf(id)}</span><X size={13} />
            </button>
          ))}
        </div>
      )}
      <div style={{ position: "relative", marginBottom: 6 }}>
        <Search size={16} color={C.faint} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("搵", "Search ") + noun} style={{ ...inputStyle, paddingLeft: 34, fontSize: 14, padding: "9px 10px 9px 34px" }} />
      </div>
      <div style={{ maxHeight: 260, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {all.length === 0 && <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, fontSize: 13, padding: 16, margin: 0 }}>{zhEn("未有", "No ")}{noun}{kind === "tag" ? zhEn("（喺商品「網店」頁加標籤）", " (add tags on the product's \"Online\" page)") : ""}</p>}
        {shown.map(o => {
          const on = sel.has(o.id);
          return (
            <button key={o.id} type="button" onClick={() => toggle(o.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", border: "none", borderBottom: "1px solid " + C.line, background: "none", fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
              <span style={{ width: 22, height: 22, borderRadius: 7, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: on ? C.accent : "#fff", border: "1.5px solid " + (on ? C.accent : C.faint) }}>{on && <Check size={14} color="#fff" strokeWidth={3.2} />}</span>
              {o.p && <ProductThumb p={o.p} size={34} radius={10} />}
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 800, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name}</span>
              {o.p && <span style={{ fontSize: 12.5, fontWeight: 800, color: C.sub }}>{money(o.p.price, sym)}</span>}
            </button>
          );
        })}
        {list.length > shown.length && <p style={{ textAlign: "center", color: C.sub, fontWeight: 700, fontSize: 12, margin: "8px 0 2px" }}>{zhEn("仲有", "There are")} {list.length - shown.length} {zhEn("項 — 用搜尋收窄", "more — narrow it down with search")}</p>}
      </div>
    </div>
  );
}
